import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, fmtErr, fmtSize, fmtTime, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fitHints, FIT_SEV, FIT_CLS, FIT_LABEL } from "@/lib/mediafit";
import { Pencil } from "lucide-react";
import { PageHeader, EmptyState } from "@/components/bits";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { UploadCloud, Trash2, Eye, Film } from "lucide-react";
import Playlists from "@/pages/Playlists";
import SchedulesPanel from "@/components/SchedulesPanel";

export default function MediaLibrary() {
  const { user } = useAuth();
  const isSuper = user?.role === "SUPER_ADMIN";
  const [tab, setTab] = useState("medien");
  const [media, setMedia] = useState([]);
  const [tags, setTags] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const [nameDraft, setNameDraft] = useState("");

  async function saveRenameMedia(id) {
    const name = nameDraft.trim();
    if (!name) return;
    try {
      await api.patch(`/media/${id}`, { filename: name });
      toast.success("Dateiname geändert");
      setRenamingId(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }
  const [devices, setDevices] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInput = useRef(null);

  useEffect(() => {
    if (isSuper) {
      api.get("/tenants").then((r) => {
        setTenants(r.data);
        if (r.data.length) setTenantId((cur) => cur || r.data[0].id);
      }).catch(() => {});
    }
  }, [isSuper]);

  const load = useCallback(() => {
    api.get("/media").then((r) => setMedia(r.data)).catch(() => {});
    api.get("/devices").then((r) => setDevices(r.data)).catch(() => {});
    api.get("/playlists").then((r) => setPlaylists(r.data)).catch(() => {});
  }, []);

  // Schlechtester Passform-Hinweis über alle Geräte, deren Playlist das Medium enthält
  function worstFit(m) {
    const plWithMedia = new Set(
      playlists.filter((p) => (p.items || []).some((it) => it.media_id === m.id)).map((p) => p.id)
    );
    let worst = null;
    for (const d of devices) {
      if (!d.playlist_id || !plWithMedia.has(d.playlist_id)) continue;
      const hints = fitHints(m, { sw: d.state?.screen_width, sh: d.state?.screen_height, rotation: d.rotation });
      for (const h of hints) {
        if (!worst || FIT_SEV[h.level] > FIT_SEV[worst.level]) worst = { ...h, device: d.display_name || d.label };
      }
    }
    return worst;
  }

  useEffect(() => {
    load();
  }, [load]);

  async function upload(files) {
    if (!files.length) return;
    if (isSuper && !tenantId) {
      toast.error("Bitte zuerst einen Mandanten wählen");
      return;
    }
    setBusy(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("tags", tags);
      if (isSuper) fd.append("tenant_id", tenantId);
      try {
        await api.post("/media", fd);
        toast.success(`Medium hochgeladen: ${f.name}`);
      } catch (e) {
        toast.error(`${f.name}: ${fmtErr(e)}`);
      }
    }
    setBusy(false);
    load();
  }

  async function remove(m) {
    try {
      await api.delete(`/media/${m.id}`);
      toast.success("Medium gelöscht");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }

  return (
    <div data-testid="media-page">
      <PageHeader title="Mediathek" sub="Medien und Wiedergabelisten verwalten — Auslieferung über signierte URLs">
        {isSuper && tab === "medien" && (
          <select
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            className="input-dark !w-56"
            data-testid="media-tenant-select"
          >
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>Upload: {t.name}</option>
            ))}
          </select>
        )}
      </PageHeader>

      <div className="flex gap-1 mb-6 border-b border-border" data-testid="media-tabs">
        {[
          { k: "medien", l: "Medien" },
          { k: "playlists", l: "Wiedergabelisten" },
          { k: "zeitplaene", l: "Zeitpläne" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 font-mono text-[11px] uppercase tracking-widest border-b-2 -mb-px transition-colors duration-150 ${
              tab === t.k ? "border-cyan-400 text-cyan-300" : "border-transparent text-slate-300 hover:text-slate-300"
            }`}
            data-testid={`media-tab-${t.k}`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {tab === "playlists" ? (
        <Playlists embedded />
      ) : tab === "zeitplaene" ? (
        <SchedulesPanel />
      ) : (
        <>
      <div
        className={`panel p-8 mb-6 border-dashed text-center cursor-pointer transition-colors duration-150 ${
          dragOver ? "border-cyan-400/70 bg-cyan-500/5" : "hover:border-cyan-500/40"
        }`}
        onClick={() => fileInput.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          upload(Array.from(e.dataTransfer.files || []));
        }}
        data-testid="media-upload-dropzone"
      >
        <UploadCloud className="mx-auto text-cyan-400 mb-3" size={28} strokeWidth={1.5} />
        <div className="text-sm text-slate-300">{busy ? "Lade hoch…" : "Dateien hierher ziehen oder klicken"}</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mt-1">
          JPEG · PNG · WebP · MP4/H.264 · max. 200 MB
        </div>
        <input
          ref={fileInput}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp,video/mp4"
          className="hidden"
          onChange={(e) => {
            upload(Array.from(e.target.files || []));
            e.target.value = "";
          }}
          data-testid="media-upload-input"
        />
        <div className="mt-4 max-w-xs mx-auto">
          <input
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            className="input-dark text-center"
            placeholder="Tags (kommagetrennt), z. B. aktion, frühling"
            data-testid="media-tags-input"
          />
        </div>
      </div>

      {media.length ? (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="media-grid">
          {media.map((m) => { const fit = worstFit(m); return (
            <div key={m.id} className="panel overflow-hidden hover:border-cyan-500/40 transition-colors duration-200" data-testid={`media-card-${m.id}`}>
              <div className="aspect-video bg-black/60 flex items-center justify-center overflow-hidden">
                {m.mime.startsWith("video/") ? (
                  <div className="relative w-full h-full">
                    <video src={fileUrl(m.url)} className="w-full h-full object-cover" muted preload="metadata" />
                    <Film className="absolute bottom-1.5 right-1.5 text-cyan-300" size={14} />
                  </div>
                ) : (
                  <img src={fileUrl(m.url)} alt={m.filename} className="w-full h-full object-cover" loading="lazy" />
                )}
              </div>
              <div className="p-3">
                {renamingId === m.id ? (
                  <div className="flex gap-1 items-center">
                    <input
                      value={nameDraft}
                      onChange={(e) => setNameDraft(e.target.value)}
                      className="input-dark !py-1 !text-xs flex-1"
                      autoFocus
                      onKeyDown={(e) => { if (e.key === "Enter") saveRenameMedia(m.id); if (e.key === "Escape") setRenamingId(null); }}
                      data-testid={`media-rename-input-${m.id}`}
                    />
                    <button onClick={() => saveRenameMedia(m.id)} className="text-cyan-300 text-xs px-1" data-testid={`media-rename-save-${m.id}`}>OK</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 group">
                    <div className="text-xs text-slate-200 truncate" title={m.filename}>{m.filename}</div>
                    <button
                      onClick={() => { setRenamingId(m.id); setNameDraft(m.filename); }}
                      className="text-slate-500 hover:text-cyan-300 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                      title="Dateiname ändern"
                      data-testid={`media-rename-${m.id}`}
                    >
                      <Pencil size={11} />
                    </button>
                  </div>
                )}
                <div className="font-mono text-[10px] text-slate-400 mt-0.5">
                  {m.mime} · {fmtSize(m.size_bytes)} · {m.width ? `${m.width} × ${m.height}` : "Maße unbekannt"}
                </div>
                {m.tags?.length ? (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {m.tags.map((t) => (
                      <span key={t} className="px-1.5 py-0.5 rounded-sm bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 font-mono text-[9px] uppercase tracking-widest">
                        {t}
                      </span>
                    ))}
                  </div>
                ) : null}
                {fit && (
                  <div className={`mt-2 px-2 py-1.5 rounded-sm border font-mono text-[10px] leading-snug ${FIT_CLS[fit.level]}`} title={fit.device ? `Gerät: ${fit.device}` : undefined} data-testid={`media-fit-${m.id}`}>
                    {FIT_LABEL[fit.level]}: {fit.text}{fit.device ? ` — ${fit.device}` : ""}
                  </div>
                )}
                <div className="flex gap-1 mt-3">
                  <button onClick={() => setPreview(m)} className="btn-ghost flex-1 !py-1 text-[11px]" data-testid={`media-preview-${m.id}`}>
                    <Eye size={12} /> Vorschau
                  </button>
                  <button onClick={() => remove(m)} className="btn-ghost !py-1 !px-2 text-[11px] hover:!border-red-500/50 hover:!text-red-400" data-testid={`media-delete-${m.id}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ); })}
        </div>
      ) : (
        <EmptyState text="Mediathek ist leer — erste Datei hochladen" testid="media-empty" />
      )}
        </>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-popover border-border max-w-3xl" data-testid="media-preview-dialog">
          <DialogTitle className="sr-only">Medienvorschau</DialogTitle>
          {preview && (
            <div>
              <div className="bg-black rounded-sm overflow-hidden flex items-center justify-center max-h-[70vh]">
                {preview.mime.startsWith("video/") ? (
                  <video src={fileUrl(preview.url)} controls autoPlay muted className="max-h-[70vh] w-full object-contain" data-testid="media-preview-player" />
                ) : (
                  <img src={fileUrl(preview.url)} alt={preview.filename} className="max-h-[70vh] w-full object-contain" data-testid="media-preview-image" />
                )}
              </div>
              <div className="mt-3 flex items-center justify-between">
                <div className="text-sm text-slate-300">{preview.filename}</div>
                <div className="font-mono text-[10px] text-slate-300">
                  {preview.mime} · {fmtSize(preview.size_bytes)} · {fmtTime(preview.created_at)}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
